import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { EntryList } from '../components/journal/EntryList';
import { InsightsPanel } from '../components/journal/InsightsPanel';
import { ExportButton } from '../components/journal/ExportButton';
import { listJournalEntries } from '../graphql/journal';
import type { JournalEntry } from '../types/journal';

export function JournalListPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const fetchEntries = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const client = generateClient();
      const response = (await client.graphql({
        query: listJournalEntries,
        variables: { userId: user.userId },
      })) as { data: { listJournalEntries: JournalEntry[] } };

      const fetched = response.data.listJournalEntries;

      // Sort by consumedAt descending (most recent first)
      fetched.sort(
        (a, b) => new Date(b.consumedAt).getTime() - new Date(a.consumedAt).getTime()
      );

      setEntries(fetched);
    } catch {
      setError('Could not load journal entries');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    if (headingRef.current) {
      headingRef.current.focus();
    }
  }, []);

  if (isLoading) {
    return (
      <div className="page journal-page">
        <h2 ref={headingRef} tabIndex={-1}>
          Dosing Journal
        </h2>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page journal-page">
        <h2 ref={headingRef} tabIndex={-1}>
          Dosing Journal
        </h2>
        <ErrorMessage message={error} onRetry={fetchEntries} />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="page journal-page">
        <h2 ref={headingRef} tabIndex={-1}>
          Dosing Journal
        </h2>
        <div className="empty-state">
          <p>No journal entries yet</p>
          <Link to="/journal/new" className="cta-button">
            Log Entry
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page journal-page">
      <h2 ref={headingRef} tabIndex={-1}>
        Dosing Journal
      </h2>
      <Link
        to="/journal/new"
        className="cta-button"
        style={{ marginBottom: '1.5rem', display: 'inline-block' }}
      >
        Log Entry
      </Link>
      <EntryList entries={entries} />
      <InsightsPanel entries={entries} />
      <ExportButton entries={entries} disabled={false} />
    </div>
  );
}
